// src/utils/leagueSchedule.js
// League calendar helpers (founder 2026-08-18): creators pick the exact day
// and time of EVERY round/race. A pattern generates the calendar; every row
// stays individually editable. Shared by CreateLeagueScreen and the League
// Command Center. Server mirror: functions/modules/leagues.js
// validateRoundTimes (same ordering / spacing / lead rules).

export const MIN_ROUND_SPACING_MIN = 5;   // mirrors LEAGUE_MIN_ROUND_SPACING_MIN (turnaround)
export const LEAGUE_MIN_LEAD_MS = 60 * 60 * 1000;
export const LEAGUE_ADMIN_MIN_LEAD_MS = 2 * 60 * 1000; // mirrors leagues.js admin lead
export const RACES_MIN = 2;               // mirrors LEAGUE_RACES_MIN
export const RACES_MAX = 100;             // mirrors LEAGUE_RACES_MAX

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Pattern kinds — how the generator walks from one round to the next.
//   weekly    — on the chosen weekdays (empty = the first round's weekday), same clock time
//   daily     — every day, same clock time
//   everyDays — every N days, same clock time
//   everyMin  — every N minutes (same-day sprint leagues)
export const PATTERN_KINDS = [
  { key: 'weekly', label: 'Weekly on…' },
  { key: 'daily', label: 'Every day' },
  { key: 'everyDays', label: 'Every N days' },
  { key: 'everyMin', label: 'Every N minutes' },
];

export const DEFAULT_PATTERN = { kind: 'weekly', weekdays: [], everyN: 2 };

// Local-date arithmetic (DST-safe): same clock time, N calendar days later.
export const addDays = (ms, days) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days, d.getHours(), d.getMinutes(), 0, 0).getTime();
};

/**
 * Round start AFTER `anchorMs` under the pattern.
 */
export function nextRoundAfter(anchorMs, pattern) {
  const kind = pattern?.kind || 'weekly';
  if (kind === 'daily') return addDays(anchorMs, 1);
  if (kind === 'everyDays') return addDays(anchorMs, Math.max(1, Math.floor(Number(pattern.everyN) || 1)));
  if (kind === 'everyMin') return anchorMs + Math.max(MIN_ROUND_SPACING_MIN, Math.floor(Number(pattern.everyN) || MIN_ROUND_SPACING_MIN)) * 60000;
  // weekly on chosen weekdays
  const days = Array.isArray(pattern.weekdays) && pattern.weekdays.length
    ? pattern.weekdays : [new Date(anchorMs).getDay()];
  for (let i = 1; i <= 7; i++) {
    const cand = addDays(anchorMs, i);
    if (days.includes(new Date(cand).getDay())) return cand;
  }
  return addDays(anchorMs, 7);
}

/**
 * Full calendar: `count` rounds starting at firstMs, following the pattern.
 * Weekly-on-days snaps round 1 forward to the first chosen weekday (same
 * clock time) when its current day isn't one of them — picking Tue/Thu
 * means the league starts on a Tue or Thu.
 */
export function generateRoundTimes(firstMs, count, pattern) {
  const out = [];
  let cur = firstMs;
  const days = pattern?.kind === 'weekly' && Array.isArray(pattern.weekdays) ? pattern.weekdays : [];
  if (days.length && !days.includes(new Date(cur).getDay())) cur = nextRoundAfter(cur, pattern);
  for (let i = 0; i < count; i++) {
    out.push(cur);
    cur = nextRoundAfter(cur, pattern);
  }
  return out;
}

/**
 * Resize a calendar to `count` rounds without disturbing existing rows:
 * shorter → truncate; longer → keep walking the pattern from the last row.
 */
export function resizeRoundTimes(times, count, pattern, fallbackFirstMs) {
  const src = Array.isArray(times) ? times.slice(0, count) : [];
  if (src.length === 0 && count > 0) src.push(fallbackFirstMs);
  while (src.length < count) src.push(nextRoundAfter(src[src.length - 1], pattern));
  return src;
}

/**
 * Best-guess pattern behind an existing calendar (Command Center hydration):
 * equal day gaps → everyDays/daily, equal sub-day gaps → everyMin, otherwise
 * weekly on the weekdays the rows land on (≤3 distinct) or the default.
 */
export function inferPattern(times) {
  if (!Array.isArray(times) || times.length < 2) return { ...DEFAULT_PATTERN };
  const gaps = times.slice(1).map((t, i) => t - times[i]);
  const allEq = gaps.every((g) => Math.abs(g - gaps[0]) < 60000);
  if (allEq) {
    if (gaps[0] < 24 * 3600e3) return { kind: 'everyMin', weekdays: [], everyN: Math.max(MIN_ROUND_SPACING_MIN, Math.round(gaps[0] / 60000)) };
    const days = Math.round(gaps[0] / 86400e3);
    if (days === 1) return { kind: 'daily', weekdays: [], everyN: 1 };
    if (days === 7) return { kind: 'weekly', weekdays: [], everyN: 2 };
    return { kind: 'everyDays', weekdays: [], everyN: days };
  }
  const days = [...new Set(times.map((t) => new Date(t).getDay()))].sort((a, b) => a - b);
  return { kind: 'weekly', weekdays: days.length <= 3 ? days : [], everyN: 2 };
}

/**
 * Per-row problems, mirroring the server's validateRoundTimes. Returns an
 * array (index = round-1) of null | message.
 */
export function roundTimeIssues(times, { durationMinutes = 30, nowMs = Date.now(), minLeadMs = LEAGUE_MIN_LEAD_MS, noun = 'round' } = {}) {
  // Server: validateRoundTimes — duration + turnaround between rounds.
  const spacingMs = ((Number(durationMinutes) || 30) + MIN_ROUND_SPACING_MIN) * 60000;
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1);
  const nouns = noun === 'match' ? 'matches' : `${noun}s`;
  return (times || []).map((t, i) => {
    if (!Number.isFinite(t)) return `Pick a date and time for ${noun} ${i + 1}.`;
    if (i === 0) {
      return t - nowMs < minLeadMs
        ? `${Noun} 1 must start at least ${Math.round(minLeadMs / 60000)} minutes from now so the roster can fill.`
        : null;
    }
    const prev = times[i - 1];
    if (!Number.isFinite(prev)) return null;
    if (t <= prev) return `${Noun} ${i + 1} is before ${noun} ${i} — ${nouns} run in order.`;
    if (t - prev < spacingMs) return `Too close to ${noun} ${i} — leave at least ${Math.round(spacingMs / 60000)} minutes.`;
    return null;
  });
}

// ── Display helpers ─────────────────────────────────────────────────────────

export const fmtWhen = (ms) => {
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

export const fmtDay = (ms) => {
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const fmtSpan = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const hrs = min / 60;
  if (hrs < 48) return `${hrs.toFixed(hrs < 10 ? 1 : 0).replace(/\.0$/, '')} hours`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `${days} days`;
  if (days < 70) return `${(days / 7).toFixed(1).replace(/\.0$/, '')} weeks`;
  return `${(days / 30.4).toFixed(1).replace(/\.0$/, '')} months`;
};

/**
 * One-line rhythm summary of a calendar ("Tue & Thu at 8:00 PM", "Daily at
 * 7:30 PM", "Every 12 min", "Sep 3, Sep 10, …"). Best-effort — falls back to
 * "Custom dates" when the rows don't share a clock time.
 */
export function describeCalendar(times) {
  if (!Array.isArray(times) || times.length === 0) return '';
  if (times.length === 1) return fmtWhen(times[0]);
  const t0 = new Date(times[0]);
  const clock = t0.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const sameClock = times.every((t) => {
    const d = new Date(t);
    return d.getHours() === t0.getHours() && d.getMinutes() === t0.getMinutes();
  });
  const gaps = times.slice(1).map((t, i) => t - times[i]);
  const allEq = gaps.every((g) => Math.abs(g - gaps[0]) < 60000);
  if (!sameClock) {
    if (allEq && gaps[0] < 24 * 3600e3) {
      const m = Math.round(gaps[0] / 60000);
      return m % 60 === 0 ? `Every ${m / 60} hour${m === 60 ? '' : 's'}` : `Every ${m} min`;
    }
    return 'Custom dates';
  }
  const dayNames = [...new Set(times.map((t) => new Date(t).getDay()))].sort((a, b) => a - b);
  if (allEq) {
    const days = Math.round(gaps[0] / 86400e3);
    if (days === 1) return `Daily at ${clock}`;
    if (days === 7) return `Every ${WEEKDAYS[t0.getDay()]} at ${clock}`;
    if (days === 14) return `Every other ${WEEKDAYS[t0.getDay()]} at ${clock}`;
    if (days > 0) return `Every ${days} days at ${clock}`;
  }
  if (dayNames.length <= 3 && times.length >= dayNames.length + 1) {
    return `${dayNames.map((d) => WEEKDAYS[d]).join(' & ')} at ${clock}`;
  }
  return `Custom dates at ${clock}`;
}
